import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import SuperAdminLayout from './components/SuperAdminLayout';

/**
 * Super admin dashboard.
 *
 * R2 — strictly metadata only. NEVER render financial amounts here:
 *   - allowed:  tenant count, agency count, manager count, creator count, plan, status
 *   - banned:   diamonds, revenue, payout amount, commission, cashback amount
 *
 * R9 — violations are scraping-deferred Phase 2; not displayed here.
 */
const StatCard = ({ label, value, sublabel }) => (
  <div className="rounded-2xl bg-card border border-border p-5 shadow-elevation-1">
    <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
    <div className="text-3xl font-heading font-bold text-foreground mt-2 tabular-nums">{value}</div>
    {sublabel && <div className="text-xs text-muted-foreground mt-1">{sublabel}</div>}
  </div>
);

const SuperAdminDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    tenantsTotal: 0,
    tenantsActive: 0,
    agenciesTotal: 0,
    managersTotal: 0,
    creatorsTotal: 0,
  });
  const [tenantBreakdown, setTenantBreakdown] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tenantsRes, agenciesRes, managersCountRes, creatorsCountRes] = await Promise.all([
          supabase.from('tenants').select('id, slug, name, plan, active'),
          supabase.from('agencies').select('id, tenant_id, active'),
          // Managers/agents = user-facing role on the agent app
          supabase.from('profiles').select('id', { count: 'exact', head: true }).in('role', ['manager', 'agent']),
          supabase.from('creators').select('id', { count: 'exact', head: true }),
        ]);

        if (cancelled) return;

        const tenants = tenantsRes.data || [];
        const agencies = agenciesRes.data || [];

        // Per-tenant breakdown (counts only — R2)
        const breakdown = tenants.map((tnt) => {
          const tenantAgencies = agencies.filter((a) => a.tenant_id === tnt.id);
          return {
            id: tnt.id,
            slug: tnt.slug,
            name: tnt.name,
            plan: tnt.plan,
            active: tnt.active,
            agenciesCount: tenantAgencies.length,
            agenciesActive: tenantAgencies.filter((a) => a.active).length,
          };
        });

        setStats({
          tenantsTotal: tenants.length,
          tenantsActive: tenants.filter((t) => t.active).length,
          agenciesTotal: agencies.length,
          managersTotal: managersCountRes.count || 0,
          creatorsTotal: creatorsCountRes.count || 0,
        });
        setTenantBreakdown(breakdown);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <SuperAdminLayout
      title={t('superadmin.dashboard.title', 'Overview')}
      subtitle={t('superadmin.dashboard.subtitle', 'NoiRock platform — system metadata only')}
    >
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 rounded-2xl skeleton" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label={t('superadmin.stats.tenants', 'Tenants')}
              value={stats.tenantsTotal}
              sublabel={`${stats.tenantsActive} ${t('superadmin.stats.active', 'active')}`}
            />
            <StatCard
              label={t('superadmin.stats.agencies', 'Agencies')}
              value={stats.agenciesTotal}
            />
            <StatCard
              label={t('superadmin.stats.managers', 'Managers')}
              value={stats.managersTotal}
            />
            <StatCard
              label={t('superadmin.stats.creators', 'Creators')}
              value={stats.creatorsTotal}
            />
          </div>

          {/* Tenant breakdown — metadata cards */}
          <section className="mt-8">
            <h2 className="font-heading text-lg font-semibold text-foreground mb-3">
              {t('superadmin.dashboard.tenantBreakdown', 'Tenants')}
            </h2>
            <div className="grid gap-3 md:grid-cols-2">
              {tenantBreakdown.map((tnt) => (
                <Link
                  key={tnt.id}
                  to={`/superadmin/tenants/${tnt.id}`}
                  className="block rounded-xl bg-card border border-border p-4 hover:border-accent transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-heading font-semibold text-foreground">{tnt.name}</div>
                      <div className="text-xs text-muted-foreground">slug: {tnt.slug}</div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                      tnt.active
                        ? 'bg-success/10 text-success'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {tnt.active ? t('superadmin.active', 'Active') : t('superadmin.inactive', 'Inactive')}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                    <span>plan: <span className="font-medium text-foreground">{tnt.plan}</span></span>
                    <span>{tnt.agenciesCount} {t('superadmin.agencies', 'agencies')}</span>
                  </div>
                </Link>
              ))}
              {tenantBreakdown.length === 0 && (
                <div className="rounded-xl bg-card border border-dashed border-border p-6 text-center text-muted-foreground text-sm">
                  {t('superadmin.dashboard.empty', 'No tenants yet.')}
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminDashboard;
